import React, { createContext, useReducer, useEffect } from 'react'
import { FeatureProfile } from '../types/random'
import { constants } from 'src/lib/utils/constants'

export const setThemeColors = (profile: FeatureProfile) => {
  if (profile === FeatureProfile.SHOPFLO) {
    document.documentElement.style.setProperty('--flo-primary-dark-color', 'var(--flo-cyan-dark-color)')
    document.documentElement.style.setProperty('--flo-primary-light-color', 'var(--flo-cyan-light-color)')
  } else {
    document.documentElement.style.setProperty('--flo-primary-dark-color', 'var(--flo-violet-color)')
    document.documentElement.style.setProperty('--flo-primary-light-color', 'var(--flo-violet-light-color)')
  }
}

// Define state type
interface FeatureProfileState {
  profile: FeatureProfile
}

// Initial state
const initialState: FeatureProfileState = {
  profile: FeatureProfile.SHOPFLO
}

export interface FeatureProfileContextType {
  state: FeatureProfileState
  actions: {
    switchProfile: (data: any) => void
    resetProfile: () => void
  }
}

// Define action types
type FeatureProfileAction = { type: 'SWITCH_PROFILE'; payload: FeatureProfile }

// Create context
const FeatureProfileContext = createContext<FeatureProfileContextType>({
  state: initialState,
  actions: {
    switchProfile: () => {},
    resetProfile: () => {}
  }
})

// Reducer function
const profileReducer = (state: FeatureProfileState, action: FeatureProfileAction): FeatureProfileState => {
  switch (action.type) {
    case 'SWITCH_PROFILE':
      setThemeColors(action.payload)
      localStorage.setItem(constants.PROFILE_STORAGE_KEY, action.payload)
      return {
        ...state,
        profile: action.payload
      }
    default:
      return state
  }
}

export const FeatureProfileProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(profileReducer, initialState)

  useEffect(() => {
    const savedProfile = localStorage.getItem(constants.PROFILE_STORAGE_KEY)
    if (savedProfile) {
      dispatch({
        type: 'SWITCH_PROFILE',
        payload: savedProfile as FeatureProfile
      })
    } else {
      setThemeColors(initialState.profile)
    }
  }, [])

  const switchProfile = (newProfile: FeatureProfile) => {
    dispatch({
      type: 'SWITCH_PROFILE',
      payload: newProfile
    })
  }

  const resetProfile = () => {
    localStorage.removeItem(constants.PROFILE_STORAGE_KEY)
    dispatch({
      type: 'SWITCH_PROFILE',
      payload: initialState.profile
    })
  }

  const actions = {
    switchProfile,
    resetProfile
  }

  return (
    <FeatureProfileContext.Provider
      value={{
        state: state,
        actions
      }}
    >
      {children}
    </FeatureProfileContext.Provider>
  )
}

export const useFeatureProfile = () => {
  const context = React.useContext(FeatureProfileContext)
  return context
}
